import { supabase } from '../lib/supabase';
import { 
  KnowledgeNode, 
  KnowledgeRelationship, 
  KnowledgeArticle,
  KnowledgeNodeResource,
  CreateNodeForm,
  CreateRelationshipForm,
  CreateArticleForm,
  CreateResourceForm 
} from '../types/knowledge';

export class KnowledgeService {
  // Node operations
  static async getNodes(userId: string): Promise<KnowledgeNode[]> {
    const { data, error } = await supabase
      .from('knowledge_nodes')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching nodes:', error);
      throw error;
    }

    return data || [];
  }

  static async createNode(nodeData: CreateNodeForm, userId: string): Promise<KnowledgeNode> {
    const { data, error } = await supabase
      .from('knowledge_nodes')
      .insert([{ ...nodeData, user_id: userId }])
      .select()
      .single();

    if (error) {
      console.error('Error creating node:', error);
      throw error;
    }

    return data;
  }

  static async updateNode(id: string, updates: Partial<CreateNodeForm>, userId: string): Promise<KnowledgeNode> {
    const { data, error } = await supabase
      .from('knowledge_nodes')
      .update(updates)
      .eq('id', id)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) {
      console.error('Error updating node:', error);
      throw error;
    }

    return data;
  }

  static async deleteNode(id: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('knowledge_nodes')
      .delete()
      .eq('id', id)
      .eq('user_id', userId);

    if (error) {
      console.error('Error deleting node:', error);
      throw error;
    }
  }

  // Relationship operations
  static async getRelationships(userId: string): Promise<KnowledgeRelationship[]> {
    const { data, error } = await supabase
      .from('knowledge_relationships')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching relationships:', error);
      throw error;
    }

    return data || [];
  }

  static async createRelationship(relationshipData: CreateRelationshipForm, userId: string): Promise<KnowledgeRelationship> {
    const { data, error } = await supabase
      .from('knowledge_relationships')
      .insert([{ ...relationshipData, user_id: userId }])
      .select()
      .single();

    if (error) {
      console.error('Error creating relationship:', error);
      throw error;
    }

    return data;
  }

  static async updateRelationship(id: string, updates: Partial<CreateRelationshipForm>, userId: string): Promise<KnowledgeRelationship> {
    const { data, error } = await supabase
      .from('knowledge_relationships')
      .update(updates)
      .eq('id', id)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) {
      console.error('Error updating relationship:', error);
      throw error;
    }

    return data;
  }

  static async deleteRelationship(id: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('knowledge_relationships')
      .delete()
      .eq('id', id)
      .eq('user_id', userId);

    if (error) {
      console.error('Error deleting relationship:', error);
      throw error;
    }
  }

  // Article operations
  static async getArticles(userId: string): Promise<KnowledgeArticle[]> {
    const { data, error } = await supabase
      .from('knowledge_articles')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching articles:', error);
      throw error;
    }

    return data || [];
  }

  static async getArticleByNodeId(nodeId: string, userId: string): Promise<KnowledgeArticle | null> {
    const { data, error } = await supabase
      .from('knowledge_articles')
      .select('*')
      .eq('node_id', nodeId)
      .eq('user_id', userId)
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 is "not found"
      console.error('Error fetching article:', error);
      throw error;
    }

    return data || null;
  }

  static async createArticle(articleData: CreateArticleForm, userId: string): Promise<KnowledgeArticle> {
    const { data, error } = await supabase
      .from('knowledge_articles')
      .insert([{ ...articleData, user_id: userId }])
      .select()
      .single();

    if (error) {
      console.error('Error creating article:', error);
      throw error;
    }

    return data;
  }

  static async updateArticle(id: string, updates: Partial<CreateArticleForm>, userId: string): Promise<KnowledgeArticle> {
    const { data, error } = await supabase
      .from('knowledge_articles')
      .update(updates)
      .eq('id', id)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) {
      console.error('Error updating article:', error);
      throw error;
    }

    return data;
  }

  static async deleteArticle(id: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('knowledge_articles')
      .delete()
      .eq('id', id)
      .eq('user_id', userId);

    if (error) {
      console.error('Error deleting article:', error);
      throw error;
    }
  }

  // Utility methods
  static async updateNodePosition(nodeId: string, x: number, y: number, userId: string): Promise<void> {
    const { error } = await supabase
      .from('knowledge_nodes')
      .update({ x_position: x, y_position: y })
      .eq('id', nodeId)
      .eq('user_id', userId);

    if (error) {
      console.error('Error updating node position:', error);
      throw error;
    }
  }

  // Resource operations
  static async getResources(userId: string): Promise<KnowledgeNodeResource[]> {
    const { data, error } = await supabase
      .from('knowledge_node_resources')
      .select('*')
      .eq('user_id', userId)
      .order('order_index', { ascending: true })
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching resources:', error);
      throw error;
    }

    return data || [];
  }

  static async getResourcesByNodeId(nodeId: string, userId: string): Promise<KnowledgeNodeResource[]> {
    const { data, error } = await supabase
      .from('knowledge_node_resources')
      .select('*')
      .eq('node_id', nodeId)
      .eq('user_id', userId)
      .order('order_index', { ascending: true })
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching resources by node:', error);
      throw error;
    }

    return data || [];
  }

  static async createResource(resourceData: CreateResourceForm, userId: string): Promise<KnowledgeNodeResource> {
    const { data, error } = await supabase
      .from('knowledge_node_resources')
      .insert([{ ...resourceData, user_id: userId }])
      .select()
      .single();

    if (error) {
      console.error('Error creating resource:', error);
      throw error;
    }

    return data;
  }

  static async updateResource(id: string, updates: Partial<CreateResourceForm>, userId: string): Promise<KnowledgeNodeResource> {
    const { data, error } = await supabase
      .from('knowledge_node_resources')
      .update(updates)
      .eq('id', id)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) {
      console.error('Error updating resource:', error);
      throw error;
    }

    return data;
  }

  static async deleteResource(id: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('knowledge_node_resources')
      .delete()
      .eq('id', id)
      .eq('user_id', userId);

    if (error) {
      console.error('Error deleting resource:', error);
      throw error;
    }
  }

  static async reorderResources(nodeId: string, resourceIds: string[], userId: string): Promise<void> {
    const updates = resourceIds.map((id, index) => ({
      id,
      order_index: index
    }));

    for (const update of updates) {
      await supabase
        .from('knowledge_node_resources')
        .update({ order_index: update.order_index })
        .eq('id', update.id)
        .eq('user_id', userId);
    }
  }

  static async getGraphData(userId: string): Promise<{
    nodes: KnowledgeNode[];
    relationships: KnowledgeRelationship[];
    articles: KnowledgeArticle[];
    resources: KnowledgeNodeResource[];
  }> {
    const [nodes, relationships, articles, resources] = await Promise.all([
      this.getNodes(userId),
      this.getRelationships(userId),
      this.getArticles(userId),
      this.getResources(userId),
    ]);

    return { nodes, relationships, articles, resources };
  }
}
